import icEditPen from "@/assets/icons/ic-edit-pen.svg";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScopeComparison } from "@/components/ui/scope-comparison";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import type { ProjectReport } from "@/services/chat-service";
import { editProjectReport } from "@/services/project-report-service";
import React, { useCallback, useMemo, useState } from "react";
import IcInfo from "@/assets/icons/ic-info.svg";
import { cn } from "@/lib/utils";

const SECTIONS = [
  { key: "4. 보안 및 시스템 연계 요구사항" },
  { key: "5. 데이터 및 데이터 파이프라인 요건" },
  { key: "6. Agent 적합성 필터 (요약)" },
];

// Legacy fallback: old reports use "4. 보안 정책"
function getSection4Key(report: ProjectReport): string {
  if ("4. 보안 및 시스템 연계 요구사항" in report) {
    return "4. 보안 및 시스템 연계 요구사항";
  }
  if ("4. 보안 정책" in report) {
    return "4. 보안 정책";
  }
  return "4. 보안 및 시스템 연계 요구사항";
}

interface RequirementsDefinitionTabProps {
  id: string;
  report: ProjectReport;
  refresh: () => Promise<void>;
  status: string;
  initialMode?: "view" | "edit";
}

export function RequirementsDefinitionTab({
  id,
  report,
  refresh,
  status,
  initialMode = "view",
}: RequirementsDefinitionTabProps) {
  const [mode, setMode] = useState<"view" | "edit">(initialMode);
  const [editedReport, setEditedReport] = useState<ProjectReport | null>(
    initialMode === "edit" ? report : null,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const activeReport = useMemo(() => {
    return mode === "edit" && editedReport ? editedReport : report;
  }, [mode, editedReport, report]);

  const section4Key = useMemo(() => getSection4Key(activeReport), [activeReport]);

  const handleToggleEdit = useCallback(() => {
    setMode((prev) => {
      if (prev === "view") {
        setEditedReport(report);
      }
      return prev === "view" ? "edit" : "view";
    });
  }, [report]);

  const handleCancel = useCallback(() => {
    setEditedReport(report);
    setMode("view");
  }, [report]);

  const handleSubmit = useCallback(async () => {
    if (!id || !editedReport || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await editProjectReport({ project_id: id, content: editedReport });
      setMode("view");
      await refresh();
      toast({ description: "업데이트 보고서 성공" });
    } catch (error) {
      console.error("Failed to save report:", error);
    } finally {
      setIsSubmitting(false);
    }
  }, [id, editedReport, isSubmitting, toast]);

  const handleProblemSituationChange = useCallback((value: string) => {
    setEditedReport((prev) => (prev ? { ...prev, "문제 상황 정의": value } : null));
  }, []);

  const handleInScopeChange = useCallback((items: string[]) => {
    setEditedReport((prev) => {
      if (!prev) return prev;
      const current = prev["1. 적용 업무 범위"] || { "In scope": [] };
      return { ...prev, "1. 적용 업무 범위": { ...current, "In scope": items } };
    });
  }, []);

  const updateSection = useCallback((sectionKey: string, label: string, value: string) => {
    setEditedReport((prev) => {
      if (!prev) return prev;
      const section = {
        ...(prev as unknown as Record<string, Record<string, string>>)[sectionKey],
      };
      section[label] = value;
      return { ...prev, [sectionKey]: section };
    });
  }, []);

  // Build section items for sections 4-6
  const sectionItems = useMemo(() => {
    const effectiveSections = SECTIONS.map(({ key }) => {
      const effectiveKey = key === "4. 보안 및 시스템 연계 요구사항" ? section4Key : key;
      return {
        key: effectiveKey,
        displayKey: key, // Always show the new label
      };
    });

    return effectiveSections.map(({ key, displayKey }) => ({
      key,
      displayKey,
      items: (activeReport as unknown as Record<string, Record<string, unknown>>)[key] || {},
    }));
  }, [activeReport, section4Key]);

  return (
    <>
      {isSubmitting && (
        <div className="flex items-center justify-center py-20 h-[60vh] w-full">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-black/10 border-t-[#FF3B00]" />
        </div>
      )}
      <div 
        className="flex flex-col gap-6 w-full mx-auto mb-[40px]"
        style={{ display: isSubmitting ? "none" : "flex" }}
      >
      {/* Page Header */}
      <div className="flex items-center justify-between flex-col lg:flex-row">
        <div className="flex items-center gap-4">
          <div className="flex flex-col gap-1">
            <h1 className="text-[30px] leading-[36px] tracking-[-0.0213em] lg:text-[32px] lg:leading-10 lg:tracking-[-0.03em] text-[#202020]">
              AI Business Architect Analysis Report
            </h1>
            <span className="text-sm leading-[20px] tracking-[-0.01em] text-[#000000]">
              요구사항 분석 결과를 토대로 PwC 컨설팅 서비스와 AI 에이전트를 함께 제안합니다.
            </span>
          </div>
        </div>
        <div className="flex items-center gap-[21px] w-full lg:w-auto mt-4 lg:mt-0">
          {mode === "view" && status !== "completed" && (
            <Button
              onClick={handleToggleEdit}
              className="h-10 lg:h-[48px] rounded-[4px] cursor-pointer w-full lg:w-[208px]"
            >
              <img src={icEditPen} alt="edit" />
              요구사항 정의서 수정
            </Button>
          )}
        </div>
      </div>

      <div className="h-[1px] bg-[rgba(0,0,0,0.15)]" />

      {mode === "edit" && (
        <div className="flex items-center gap-[16px] p-[16px] bg-[#EBEBEB] rounded-[8px]">
          <img src={IcInfo} />
          <p className="text-[#000000] text-[16px]">
            편집 모드가 활성화되었습니다. 각 섹션을 클릭하여 수정할 수 있습니다.
          </p>
        </div>
      )}

      {/* Problem Situation */}
      <MemoizedEditableSection
        title="문제 상황 정의"
        content={activeReport["문제 상황 정의"] ?? ""}
        mode={mode}
        onChange={handleProblemSituationChange}
      />

      {/* 예산 범위 */}
      {activeReport["예산 범위"] && (
        <div className="flex flex-col gap-2 lg:gap-3">
          <div className="flex items-center gap-2">
            <h2 className="text-xs leading-4 text-[#000000]">예산 범위</h2>
          </div>
          <div
            className="rounded"
            style={{ border: "1px solid rgba(0, 0, 0, 0.15)" }}
          >
            {Object.entries(
              (activeReport["예산 범위"] as unknown as Record<string, string>) || {},
            ).map(([key, value], i, arr) => (
              <div
                key={key}
                className={`flex justify-start items-center ${
                  i < arr.length - 1 ? "border-b border-[rgba(0,0,0,0.15)]" : ""
                }`}
              >
                <p className="text-[#000000] text-xs font-bold leading-4 lg:text-base lg:font-bold w-[127px] lg:w-[240px] shrink-0 pr-0 p-4 lg:py-5 lg:pr-5 lg:pl-5">
                  {key}
                </p>
                <div className="flex-1 py-4 px-4 lg:py-3 lg:px-4">
                  {mode === "edit" ? (
                    <Input
                      value={
                        (editedReport?.["예산 범위"] as unknown as Record<string, string>)?.[key] ?? value
                      }
                      onChange={(e) => {
                        const newBudget = {
                          ...((editedReport?.["예산 범위"] as unknown as Record<string, string>) || {}),
                        };
                        newBudget[key] = e.target.value;
                        setEditedReport((prev) =>
                          prev
                            ? ({
                                ...prev,
                                "예산 범위": newBudget,
                              } as any)
                            : null,
                        );
                      }}
                      className="flex-1 h-11 text-sm"
                      placeholder="값을 입력하세요..."
                    />
                  ) : (
                    <p className="text-[#000000] text-sm leading-[20px] tracking-[-0.01em]">
                      {value || "-"}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Section 1: 적용 업무 범위 */}
      <ScopeComparison
        title="1. 적용 업무 범위"
        inScopeItems={activeReport["1. 적용 업무 범위"]?.["In scope"] ?? []}
        isEditable={mode === "edit"}
        onInScopeChange={handleInScopeChange}
      />

      {/* Section 2: 도입 목적 */}
      <div className="flex flex-col gap-2 lg:gap-3">
        <div className="flex items-center gap-2">
          <h2 className="text-xs leading-4 text-[#000000]">2. 도입 목적</h2>
        </div>
        <div
          className={cn(
            "w-full rounded-[4px]",
            mode === "edit"
              ? "px-4 py-3 border border-[rgba(0,0,0,0.15)]"
              : "bg-transparent border border-[rgba(0,0,0,0.15)] p-4 lg:px-4 lg:py-3",
          )}
        >
          {mode === "edit" ? (
            <Textarea
              value={(activeReport["2. 도입 목적"] as any)?.["도입 목적"] || ""}
              onChange={(e) => {
                setEditedReport((prev) =>
                  prev
                    ? ({ ...prev, "2. 도입 목적": { "도입 목적": e.target.value } } as any)
                    : null,
                );
              }}
              placeholder="도입 목적을 입력하세요..."
              className="w-full min-h-[70px] bg-white border-none focus:ring-0 p-0 text-sm"
            />
          ) : (
            <p className="text-sm leading-[20px] tracking-[-0.01em] text-[#000000] whitespace-pre-wrap">
              {(activeReport["2. 도입 목적"] as any)?.["도입 목적"] || "-"}
            </p>
          )}
        </div>
      </div>

      {/* Section 3: 자동화 수준 정의 */}
      <div className="flex flex-col gap-2 lg:gap-3">
        <div className="flex items-center gap-2">
          <h2 className="text-xs leading-4 text-[#000000]">3. 자동화 수준 정의</h2>
        </div>
        <div
          className="rounded"
          style={{
            border: "1px solid rgba(0, 0, 0, 0.15)",
          }}
        >
          {/* 자동화 수준 */}
          <AutomationRow
            label="자동화 수준"
            value={(activeReport["3. 자동화 수준 정의"] as any)?.["자동화 수준"] || ""}
            mode={mode}
            hasBorder={true}
            onChange={(val) => {
              setEditedReport((prev) => {
                if (!prev) return prev;
                return {
                  ...prev,
                  "3. 자동화 수준 정의": {
                    ...((prev as any)["3. 자동화 수준 정의"] || {}),
                    "자동화 수준": val,
                  },
                } as any;
              });
            }}
          />

          {/* 자동화 레벨 분류 및 세부 요건 */}
          {(activeReport["3. 자동화 수준 정의"] as any)?.["자동화 레벨 분류 및 세부 요건"] && (
            <>
              <div className="border-b border-[rgba(0,0,0,0.15)] bg-[#F9F9F9]">
                <p className="text-[#000000] text-xs font-bold leading-4 lg:text-sm lg:font-bold p-4 lg:py-3 lg:pl-5">
                  자동화 레벨 분류 및 세부 요건
                </p>
              </div>
              {Object.entries(
                (activeReport["3. 자동화 수준 정의"] as any)["자동화 레벨 분류 및 세부 요건"] || {},
              ).map(([subKey, subValue], i, arr) => (
                <AutomationRow
                  key={subKey}
                  label={`  └ ${subKey}`}
                  value={subValue as string}
                  mode={mode}
                  hasBorder={i < arr.length - 1 || !!(activeReport["3. 자동화 수준 정의"] as any)?.["Human-in-the-loop"]}
                  onChange={(val) => {
                    setEditedReport((prev) => {
                      if (!prev) return prev;
                      const section = { ...((prev as any)["3. 자동화 수준 정의"] || {}) };
                      const details = { ...(section["자동화 레벨 분류 및 세부 요건"] || {}) };
                      details[subKey] = val;
                      section["자동화 레벨 분류 및 세부 요건"] = details;
                      return { ...prev, "3. 자동화 수준 정의": section } as any;
                    });
                  }}
                />
              ))}
            </>
          )}

          {/* Human-in-the-loop */}
          <AutomationRow
            label="Human-in-the-loop"
            value={(activeReport["3. 자동화 수준 정의"] as any)?.["Human-in-the-loop"] || ""}
            mode={mode}
            hasBorder={false}
            onChange={(val) => {
              setEditedReport((prev) => {
                if (!prev) return prev;
                return {
                  ...prev,
                  "3. 자동화 수준 정의": {
                    ...((prev as any)["3. 자동화 수준 정의"] || {}),
                    "Human-in-the-loop": val,
                  },
                } as any;
              });
            }}
          />
        </div>
      </div>

      {/* Sections 4-6 */}
      {sectionItems.map(({ key, displayKey, items }) => (
        <MemoizedNestedReportSection
          key={key}
          title={displayKey}
          sectionKey={key}
          items={items as Record<string, unknown>}
          mode={mode}
          onUpdate={(label, value) => updateSection(key, label, value)}
          onUpdateNested={(parentKey, childKey, value) => {
            setEditedReport((prev) => {
              if (!prev) return prev;
              const section = { ...(prev as any)[key] };
              const nested = { ...(section[parentKey] || {}) };
              nested[childKey] = value;
              section[parentKey] = nested;
              return { ...prev, [key]: section } as any;
            });
          }}
        />
      ))}

      {mode === "edit" && (
        <div className="flex justify-center items-center gap-[8px] pt-4">
          <Button
            className="w-[175px] rounded-[4px] font-bold border-none bg-[#EBEBEB] h-[48px]"
            variant="outline"
            onClick={handleCancel}
          >
            취소
          </Button>
          <Button
            className="w-[175px] rounded-[4px] font-bold h-[48px]"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            저장
          </Button>
        </div>
      )}
      </div>
    </>
  );
}

// ──────────────────────────────────────────────────────────
// Automation Row Component
// ──────────────────────────────────────────────────────────

function AutomationRow({
  label,
  value,
  mode,
  hasBorder,
  onChange,
}: {
  label: string;
  value: string;
  mode: "view" | "edit";
  hasBorder: boolean;
  onChange?: (value: string) => void;
}) {
  return (
    <div
      className={`flex justify-start items-center ${
        hasBorder ? "border-b border-[rgba(0,0,0,0.15)]" : ""
      }`}
    >
      <p className="text-[#000000] text-xs font-bold leading-4 lg:text-base lg:font-bold w-[127px] lg:w-[240px] shrink-0 pr-0 p-4 lg:py-5 lg:pr-5 lg:pl-5">
        {label}
      </p>
      <div className="flex-1 py-4 px-4 lg:py-3 lg:px-4">
        {mode === "edit" ? (
          <Input
            value={value}
            onChange={(e) => onChange?.(e.target.value)}
            className="flex-1 h-11 text-sm"
            placeholder="값을 입력하세요..."
          />
        ) : (
          <p className="text-[#000000] text-sm leading-[20px] tracking-[-0.01em]">
            {value || "-"}
          </p>
        )}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────
// Memoized Editable Section Component
// ──────────────────────────────────────────────────────────

interface EditableSectionProps {
  title: string;
  content: string;
  mode: "view" | "edit";
  onChange?: (value: string) => void;
}

const MemoizedEditableSection = React.memo(function EditableSection({
  title,
  content,
  mode,
  onChange,
}: EditableSectionProps) {
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  const adjustHeight = React.useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, []);

  React.useEffect(() => {
    if (mode === "edit") {
      setTimeout(adjustHeight, 0);
    }
  }, [content, mode, adjustHeight]);

  return (
    <div className="flex flex-col gap-2 lg:gap-3">
      <h2 className="text-[12px] leading-4 text-[#000000]">{title}</h2>
      <div
        className={cn(
          "w-full border border-[rgba(0,0,0,0.15)] h-fit",
          mode === "edit" ? " rounded px-4 py-3" : "bg-transparent rounded p-4 lg:px-4 lg:py-3",
        )}
      >
        {mode === "edit" ? (
          <Textarea
            ref={textareaRef}
            value={content || ""}
            onChange={(e) => {
              onChange?.(e.target.value);
              adjustHeight();
            }}
            placeholder="여기에 내용을 입력하세요..."
            className="w-full bg-transparent !border-none focus:ring-0 p-0 resize-none overflow-hidden min-h-[40px]"
          />
        ) : (
          <p className="text-[14px] leading-[20px] tracking-[-0.01em] text-[#000000] whitespace-pre-wrap">
            {content}
          </p>
        )}
      </div>
    </div>
  );
});

// ──────────────────────────────────────────────────────────
// Auto Resize Textarea
// ──────────────────────────────────────────────────────────

function AutoResizeTextarea(props: React.ComponentProps<typeof Textarea>) {
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);

  const adjustHeight = React.useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, []);

  React.useEffect(() => {
    setTimeout(adjustHeight, 0);
  }, [props.value, adjustHeight]);

  return (
    <Textarea
      {...props}
      ref={textareaRef}
      onChange={(e) => {
        props.onChange?.(e);
        adjustHeight();
      }}
      className={cn("resize-none overflow-hidden", props.className)}
    />
  );
}

// ──────────────────────────────────────────────────────────
// Nested Report Section (handles dict-of-dict structures)
// ──────────────────────────────────────────────────────────

interface NestedReportSectionProps {
  title: string;
  sectionKey: string;
  items: Record<string, unknown>;
  mode?: "view" | "edit";
  onUpdate?: (label: string, value: string) => void;
  onUpdateNested?: (parentKey: string, childKey: string, value: string) => void;
}

const MemoizedNestedReportSection = React.memo(function NestedReportSection({
  title,
  sectionKey,
  items,
  mode,
  onUpdate,
  onUpdateNested,
}: NestedReportSectionProps) {
  const entries = Object.entries(items);

  return (
    <div className="flex flex-col gap-2 lg:gap-3">
      <div className="flex items-center gap-[8px]">
        <h2 className="text-[12px] leading-4 text-[#000000]">{title}</h2>
      </div>
      <div
        className="p-4 lg:p-5 rounded"
        style={{
          border: "1px solid rgba(0, 0, 0, 0.15)",
        }}
      >
        <div className="flex flex-col gap-4">
          {entries.map(([label, content]) => {
            // Handle nested dict (e.g., 시스템 연계 요구사항)
            if (typeof content === "object" && content !== null && !Array.isArray(content)) {
              const nestedEntries = Object.entries(content as Record<string, string>);
              return (
                <div key={label} className="flex flex-col gap-2">
                  <h3 className="font-bold text-[#000000]">{label}</h3>
                  <div
                    className="rounded ml-2"
                    style={{ border: "1px solid rgba(0, 0, 0, 0.08)" }}
                  >
                    {nestedEntries.map(([subLabel, subContent], si, sarr) => (
                      <div
                        key={subLabel}
                        className={`flex flex-col lg:flex-row ${
                          si < sarr.length - 1 ? "border-b border-[rgba(0,0,0,0.08)]" : ""
                        }`}
                      >
                        <p className="text-[#404040] text-xs font-semibold leading-4 lg:text-sm w-full lg:w-[200px] shrink-0 p-3 lg:p-4 bg-[#FAFAFA]">
                          {subLabel}
                        </p>
                        <div className="flex-1 p-3 lg:p-4">
                          {mode === "edit" ? (
                            <AutoResizeTextarea
                              value={subContent || ""}
                              onChange={(e) => onUpdateNested?.(label, subLabel, e.target.value)}
                              placeholder="여기에 내용을 입력하세요..."
                              className="w-full min-h-[40px] rounded-[4px]"
                            />
                          ) : subContent ? (
                            <p className="text-[14px] leading-[20px] tracking-[-0.01em] text-[#000000] whitespace-pre-wrap">
                              {subContent}
                            </p>
                          ) : (
                            <p className="text-base text-[#BBBBBB]">-</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            }

            // Handle regular string fields
            return (
              <div key={label} className="flex flex-col gap-2">
                <h3 className="font-bold text-[#000000]">{label}</h3>
                {mode === "edit" ? (
                  <AutoResizeTextarea
                    value={(content as string) || ""}
                    onChange={(e) => onUpdate?.(label, e.target.value)}
                    placeholder="여기에 내용을 입력하세요..."
                    className="w-full min-h-[64px] rounded-[4px]"
                  />
                ) : content ? (
                  <p className="text-[14px] leading-[20px] tracking-[-0.01em] text-[#000000] whitespace-pre-wrap">
                    {content as string}
                  </p>
                ) : (
                  <p className="text-base text-[#BBBBBB]">-</p>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
});
